"use client";

import React from "react";
import { Cpu, Mail, Phone, MapPin, Clock, Layers, ChevronRight, Compass, Building2 } from 'lucide-react';
import { useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
interface NavChild {
  id: string;
  label: string;
  href?: string;
  children?: NavChild[];
}
function useLanguage() {
  const [lang, setLang] = React.useState<"en" | "zh">("en");
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const saved = localStorage.getItem("mf_lang");
      if (saved === "zh" || saved === "en") {
        setLang(saved);
      }
    } catch {
      // ignore
    }
    const handleCustomEvent = (e: Event) => {
      const customEvt = e as CustomEvent<{
        lang?: "en" | "zh";
      }>;
      if (customEvt.detail?.lang && (customEvt.detail.lang === "zh" || customEvt.detail.lang === "en")) {
        setLang(customEvt.detail.lang);
      }
    };
    const handleStorageEvent = (e: StorageEvent) => {
      if (e.key === "mf_lang" && (e.newValue === "zh" || e.newValue === "en")) {
        setLang(e.newValue);
      }
    };
    window.addEventListener("mf-language-change", handleCustomEvent);
    window.addEventListener("storage", handleStorageEvent);
    return () => {
      window.removeEventListener("mf-language-change", handleCustomEvent);
      window.removeEventListener("storage", handleStorageEvent);
    };
  }, []);
  return lang;
}
const menuLabelMapZh: Record<string, string> = {
  "Stainless Steel Mesh": "不锈钢丝网",
  "Industrial Applications": "工业应用",
  "Alloy Specifications": "合金规格",
  "Technical Resources & Calculators": "技术资源与计算工具",
  "Home": "首页",
  "Products": "产品",
  "Applications": "应用领域",
  "Materials": "材料与合金",
  "Technical Resources": "技术资源",
  "Quality & Supply": "质量与供应",
  "Quality and Supply": "质量与供应",
  "About": "关于我们",
  "Contact": "联系我们",
  "Polymer Filtration": "聚合物过滤",
  "Chemical & Process Filtration": "化学与工艺过滤",
  "Oil, Fuel & Petrochemical Filtration": "油品、燃料及石化过滤",
  "Water & Liquid Filtration": "水与液体过滤",
  "Food & Pharmaceutical Filtration": "食品与医药过滤",
  "Screening & Separation": "筛分与分离",
  "Specialty Weaves": "特种织造",
  "5-Layer": "5层烧结网",
  "Multi-Layer": "多层复合网",
  "Custom": "定制规格",
  "Filter Discs": "滤圆片",
  "Filter Screens": "过滤网屏",
  "Filter Packs": "组合滤网",
  "Filter Elements": "滤芯组件",
  "Gas & Air Filtration": "气体与空气过滤",
  "Weave Types": "编织类型",
  "Plain & Twilled Weave": "平纹及斜纹编织",
  "Dutch & Reverse Dutch Weave": "荷兰及反向荷兰编织",
  "Materials & Surface": "材质与表面处理",
  "Sintered Wire Mesh": "烧结丝网",
  "Woven Wire Mesh": "编织丝网",
  "Filter Components": "过滤组件",
  "Plain Weave Wire Mesh": "平纹编织丝网",
  "Twilled Weave Wire Mesh": "斜纹编织丝网",
  "Dutch Weave Wire Mesh": "荷兰编织丝网",
  "Mesh Fundamentals": "丝网基础知识",
  "Specifications & Selection": "规格与选型",
  "Specifications Selection": "规格与选型",
  "Materials Grades": "材质等级",
  "Materials & Grades": "材质等级",
  "Technical Guides Downloads": "技术指南与下载",
  "Technical Guides & Downloads": "技术指南与下载",
  "Calculation Guides": "计算指南",
  "Selection Guides": "选型指南",
  "Technical Charts": "技术图表",
  "PDF Downloads": "PDF技术手册",
  "Buying Guides": "采购指南",
  "Datasheets": "数据表",
  "Standards & Inspection": "标准与检测",
  "Standards Inspection": "标准与检测",
  "ASTM E2016 Explained": "ASTM E2016标准解析",
  "Wire Mesh Inspection Guide": "丝网检测指南",
  "ASTM E2016 Scope & Limitations": "ASTM E2016适用范围与限制",
  "ASTM E2016 Scope Limitations": "ASTM E2016适用范围与限制",
  "How Wire Mesh Aperture Is Measured": "丝网孔径测量方法",
  "ISO Wire Mesh Standards": "ISO丝网标准",
  "Stainless Steel Material Verification": "不锈钢材质鉴定",
  "Wedge Wire Screen": "约翰逊楔形滤网",
  "Flat Panel Screens": "平板滤网",
  "Cylindrical Screens": "圆筒滤网",
  "Screen Tubes": "筛管",
  "Filter Nozzles": "滤水帽/喷嘴",
  "Sitemap": "网站地图"
};
function normalizeLabel(text: string): string {
  if (!text) return "";
  return text.replace(/&#0?38;/g, "&").replace(/&amp;/g, "&").replace(/\s+/g, " ").trim();
}
function translateMenuItems(items: NavChild[], isZh: boolean): NavChild[] {
  if (!isZh) return items;
  return items.map(item => {
    const normalized = normalizeLabel(item.label);
    const translated = menuLabelMapZh[normalized] || menuLabelMapZh[item.label] || item.label;
    return {
      ...item,
      label: translated,
      children: item.children ? translateMenuItems(item.children, isZh) : undefined
    };
  });
}
function RecursiveFooterMenu({
  items,
  depth = 0
}: {
  items: NavChild[];
  depth?: number;
}) {
  if (!items || items.length === 0) return null;
  return <ul className={depth === 0 ? "space-y-2.5 text-sm" : "mt-2 space-y-2 border-l border-white/15 pl-3 text-xs"}>
      {items.map((item, i) => <li key={item.id || i} data-index={i}>
          <Link to={item.href || "#"} className="group inline-flex items-center gap-1.5 text-[#D7DFEA] transition-colors hover:text-white break-words focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-sm">
            <ChevronRight className="size-3 shrink-0 text-[#D7DFEA]/60 transition-transform group-hover:translate-x-0.5 group-hover:text-white" />
            <span className="break-words">{item.label}</span>
          </Link>
          {item.children && item.children.length > 0 && <RecursiveFooterMenu items={item.children} depth={depth + 1} />}
        </li>)}
    </ul>;
}
function FooterMenuConsumer({
  isZh
}: {
  isZh: boolean;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-2 py-1">
        <div className="h-4 w-28 animate-pulse rounded bg-white/10" />
        <div className="h-4 w-36 animate-pulse rounded bg-white/10" />
        <div className="h-4 w-24 animate-pulse rounded bg-white/10" />
        <div className="h-4 w-32 animate-pulse rounded bg-white/10" />
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    const fallbackNav: NavChild[] = [{
      id: "fn-1",
      label: isZh ? "不锈钢丝网" : "Stainless Steel Mesh",
      href: "/ProductsPage"
    }, {
      id: "fn-2",
      label: isZh ? "工业应用" : "Industrial Applications",
      href: "/ApplicationsPage"
    }, {
      id: "fn-3",
      label: isZh ? "合金规格" : "Alloy Specifications",
      href: "/MaterialsPage"
    }, {
      id: "fn-4",
      label: isZh ? "技术资源与计算工具" : "Technical Resources & Calculators",
      href: "/TechnicalResourcesPage"
    }];
    return <RecursiveFooterMenu items={fallbackNav} />;
  }
  const processedItems = translateMenuItems(menuItems, isZh);
  return <RecursiveFooterMenu items={processedItems} />;
}
export default function Footer() {
  const shouldReduceMotion = useReducedMotion();
  const lang = useLanguage();
  const isZh = lang === "zh";
  const technicalDivisions = [{
    title: isZh ? "网格制造" : "Mesh Fabrication",
    icon: Layers,
    chipClass: "bg-white/10 text-[#D7DFEA]",
    items: [{
      label: isZh ? "编织丝网" : "Woven Wire Mesh",
      href: "/WovenWireMeshPage"
    }, {
      label: isZh ? "烧结丝网" : "Sintered Wire Mesh",
      href: "/SinteredWireMeshPage"
    }, {
      label: isZh ? "过滤组件" : "Filter Components",
      href: "/FilterComponentsPage"
    }, {
      label: isZh ? "约翰逊楔形滤网" : "Wedge Wire Screen",
      href: "/WedgeWireScreenPage"
    }],
    link: {
      to: "/SinteredWireMeshPage"
    }
  }, {
    title: isZh ? "工程资源" : "Engineering Resources",
    icon: Cpu,
    chipClass: "bg-white/10 text-[#D7DFEA]",
    items: [{
      label: isZh ? "孔径及微米等级图表" : "Aperture & Micron Rating Charts",
      href: "/ApertureMicronRatingChartsPage"
    }, {
      label: isZh ? "差压(Delta-P)模型" : "Differential Pressure (Delta-P) Models",
      href: "/DifferentialPressureDeltaPModelsPage"
    }, {
      label: isZh ? "耐腐蚀性矩阵" : "Corrosion Resistance Matrices",
      href: "/CorrosionResistanceMatricesPage"
    }, {
      label: isZh ? "OEM定制网格工程" : "OEM Custom Mesh Engineering",
      href: "/OEMCustomMeshEngineeringPage"
    }]
  }, {
    title: isZh ? "公司" : "Company",
    icon: Building2,
    chipClass: "bg-white/10 text-[#D7DFEA]",
    items: [{
      label: isZh ? "关于我们" : "About",
      href: "/AboutPage"
    }, {
      label: isZh ? "制造能力" : "Capabilities",
      href: "/CapabilitiesPage"
    }, {
      label: isZh ? "质量与供应" : "Quality & Supply",
      href: "/QualitySupplyPage"
    }, {
      label: isZh ? "网站地图" : "Site map",
      href: "/SitemapPage"
    }]
  }];
  return <section id="corporate-footer" data-nav="dark" className="relative border-t border-border bg-gradient-to-b from-[#121821] via-[#121821] to-[#1a2230] text-white overflow-hidden">
      <div className="absolute top-0 inset-x-0 h-0.5 bg-gradient-to-r from-primary via-primary/50 to-transparent" />

      <div className="container mx-auto px-6 py-16 md:px-12 md:py-20">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-8">
          <div className="hidden lg:block lg:col-span-5">
            <div className="flex items-center">
              <Link to="/" className="inline-flex items-center">
                <WvcLogo className="h-9 sm:h-10 w-auto max-w-[200px] sm:max-w-[240px] object-contain [&_svg]:h-9 [&_svg]:sm:h-10 [&_svg]:w-auto [&_svg]:max-w-[200px] [&_svg]:sm:max-w-[240px] [&_svg]:object-contain !text-white [&_svg]:!text-white" />
              </Link>
            </div>

            <p className="mt-4 max-w-md text-sm leading-relaxed text-[#B8C0CA]">MutualFilter is an industrial filtration, screening and separation brand operated by Hebei Hutuo Wire Mesh Products Co., Ltd., providing woven wire mesh, sintered wire mesh, filter components and wedge wire screens for industrial applications.</p>

            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="rounded-lg border border-white/15 bg-white/5 p-3.5 shadow-xs transition-[border-color] hover:border-primary/40">
                <div className="flex items-center gap-2 text-xs font-semibold tracking-wide text-[#B8C0CA] uppercase">
                  <div className="flex size-6 shrink-0 items-center justify-center rounded-md bg-[#D7DFEA]/15 text-[#D7DFEA]">
                    <MapPin className="size-3.5" />
                  </div>
                  <span>{isZh ? "设施" : "Facility"}</span>
                </div>
                <div className="mt-2 text-xs text-white pl-8 break-words">
                  <div>{isZh ? "河北安平" : "Anping, Hebei"}</div>
                  <div>{isZh ? "南后团村东800米" : "800 meters east of Nanhoutuan Village"}</div>
                </div>
              </div>

              <div className="rounded-lg border border-white/15 bg-white/5 p-3.5 shadow-xs transition-[border-color] hover:border-primary/40">
                <div className="flex items-center gap-2 text-xs font-semibold tracking-wide text-[#B8C0CA] uppercase">
                  <div className="flex size-6 shrink-0 items-center justify-center rounded-md bg-[#D7DFEA]/15 text-[#D7DFEA]">
                    <Clock className="size-3.5" />
                  </div>
                  <span>{isZh ? "技术运营" : "Technical Operations"}</span>
                </div>
                <p className="mt-2 text-xs text-white pl-8 break-words">
                  {isZh ? "周一至周五：上午8:00 - 下午5:00" : "Mon-Fri: 8:00 AM - 5:00 PM"}
                </p>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-4 pt-2 text-xs">
              <a href="mailto:sales@mutualfilter.com" className="group inline-flex items-center gap-2 font-medium text-white transition-colors hover:text-[#D7DFEA] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-sm">
                <div className="flex size-6 shrink-0 items-center justify-center rounded-md bg-[#D7DFEA]/15 text-[#D7DFEA] transition-colors group-hover:bg-[#D7DFEA] group-hover:text-[#121821]">
                  <Mail className="size-3.5" />
                </div>
                <span className="break-all">sales@mutualfilter.com</span>
              </a>
              <a href="tel:+8618033666533" className="group inline-flex items-center gap-2 font-medium text-white transition-colors hover:text-[#D7DFEA] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-sm">
                <div className="flex size-6 shrink-0 items-center justify-center rounded-md bg-[#D7DFEA]/15 text-[#D7DFEA] transition-colors group-hover:bg-[#D7DFEA] group-hover:text-[#121821]">
                  <Phone className="size-3.5" />
                </div>
                <span>(86) 180-3366-6533</span>
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            {/* Mobile Collapsible Navigation (below sm breakpoint) */}
            <div className="block sm:hidden">
              <Accordion type="multiple" className="w-full space-y-2">
                <AccordionItem value="footer-products" className="border border-white/15 rounded-lg bg-white/5 px-3.5 shadow-2xs">
                  <AccordionTrigger className="py-3 hover:no-underline text-left focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-md">
                    <div className="flex items-center gap-2">
                      <div className="flex size-5 shrink-0 items-center justify-center rounded-md bg-white/10 text-[#D7DFEA]">
                        <Compass className="size-3" />
                      </div>
                      <span className="font-default text-xs font-semibold tracking-wider text-white uppercase">
                        {isZh ? "产品" : "Products"}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-3 pt-1">
                    <MenuProvider menu_id="5" className="w-full">
                      <FooterMenuConsumer isZh={isZh} />
                    </MenuProvider>
                  </AccordionContent>
                </AccordionItem>

                {technicalDivisions.map((division, idx) => {
                const IconComp = division.icon;
                return <AccordionItem key={division.title} value={`footer-division-${idx}`} data-index={idx} className="border border-white/15 rounded-lg bg-white/5 px-3.5 shadow-2xs">
                      <AccordionTrigger className="py-3 hover:no-underline text-left focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-md">
                        <div className="flex items-center gap-2">
                          <div className="flex size-5 shrink-0 items-center justify-center rounded-md bg-white/10 text-[#D7DFEA]">
                            <IconComp className="size-3" />
                          </div>
                          <span className="font-default text-xs font-semibold tracking-wider text-white uppercase">
                            {division.title}
                          </span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pb-3 pt-1">
                        <ul className="space-y-2.5 text-sm">
                          {division.items.map((item, itemIdx) => <li key={item.label} data-index={itemIdx}>
                              <Link to={item.href} className="group inline-flex items-center gap-1.5 text-[#D7DFEA] transition-colors hover:text-white break-words focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-sm">
                                <ChevronRight className="size-3 shrink-0 text-[#D7DFEA]/60 transition-transform group-hover:translate-x-0.5 group-hover:text-white" />
                                <span className="break-words">{item.label}</span>
                              </Link>
                            </li>)}
                        </ul>
                      </AccordionContent>
                    </AccordionItem>;
              })}
              </Accordion>
            </div>

            {/* Desktop / Tablet Navigation (sm breakpoint and above) */}
            <div className="hidden sm:grid sm:grid-cols-3 gap-8">
              {technicalDivisions.map((division, idx) => {
              const IconComp = division.icon;
              return <div key={division.title} data-index={idx}>
                    <div className="flex items-center gap-2">
                      <div className="flex size-5 shrink-0 items-center justify-center rounded-md bg-white/10 text-[#D7DFEA]">
                        <IconComp className="size-3" />
                      </div>
                      {division.link ? <Link to={division.link.to} className="font-default text-xs font-semibold tracking-wider text-white uppercase hover:text-[#D7DFEA] transition-colors">
                          <h3>{division.title}</h3>
                        </Link> : <h3 className="font-default text-xs font-semibold tracking-wider text-white uppercase">
                          {division.title}
                        </h3>}
                    </div>
                    <ul className="mt-4 space-y-2.5 text-sm">
                      {division.items.map((item, itemIdx) => <li key={item.label} data-index={itemIdx}>
                          <Link to={item.href} className="group inline-flex items-center gap-1.5 text-[#D7DFEA] transition-colors hover:text-white break-words focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-sm">
                            <ChevronRight className="size-3 shrink-0 text-[#D7DFEA]/60 transition-transform group-hover:translate-x-0.5 group-hover:text-white" />
                            <span className="break-words">{item.label}</span>
                          </Link>
                        </li>)}
                    </ul>
                  </div>;
            })}
            </div>
          </div>
        </div>

        <div className="mt-12 border-t border-white/15 pt-6">
          <div className="flex flex-col items-center justify-between gap-4 text-xs text-[#B8C0CA] sm:flex-row">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <span className="font-mono text-[11px]">
                {isZh ? "ISO认证工业网格工程" : "ISO-Compliant Industrial Mesh Engineering"}
              </span>
              <span className="hidden h-3 w-px bg-white/15 sm:inline-block" />
              <span>
                {isZh ? "完整原材料可追溯性(EN 10204 3.1)" : "Full Raw Material Traceability (EN 10204 3.1)"}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <span className="text-[#B8C0CA]/70">
                {isZh ? `© ${new Date().getFullYear()} MutualFilter. 保留所有权利。` : `© ${new Date().getFullYear()} MutualFilter. All rights reserved.`}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}